import type * as ents from '@attain-apps/entities'

import type * as models from './models'
type GlobalRecord<K extends keyof any, T> = Record<K, T>

/**
 * {@link https://ngrx.io/docs State} of this feature
 */
export interface State {
  // TODO: Add loading for adding records
  // NOTE: It would be a lot better if we would have some stack like structure
  // to work with this since we want that kind of behavior
  /** Array of entities records that are being added through reference input */
  entries: State.Entry[]
  /** @see {State.Lookup} */
  lookup: State.Lookup
}
export namespace State {
  export const INITIAL: State = {
    entries: [],
    lookup: {},
  }

  /**
   * Maximum number of **records** (across all entities) to keep in the lookup cache.
   * When exceeded, entire entity entries are evicted (least-recently-accessed first)
   * until the total record count is at or below this limit.
   */
  export const MAX_CACHED_RECORDS = 10_000

  export interface Entry {
    entityId: ents.core.dms.models.Entity.Id
    record?: ents.core.dms.data.Record.Input
  }

  export interface LookupEntry {
    /**
     * `true` when the cached records represent **all** records for the entity
     * (i.e. the last successful fetch had no `recordIds` filter).
     * Used by the cache check to avoid skipping requests when only a
     * partial set of records was previously loaded.
     */
    complete?: boolean
    records?: readonly models.Record[]
    loading?: boolean
    error?: string | Error
    /** Unix timestamp (ms) of last access — used for LRU eviction */
    lastAccessed?: number
  }

  export type Lookup = Partial<
    GlobalRecord<ents.core.dms.models.Entity.Id, LookupEntry>
  >

  /**
   * Counts the total number of records stored across all entity entries.
   */
  function totalRecords(lookup: Lookup): number {
    let count = 0
    for (const key of Object.keys(lookup) as ents.core.dms.models.Entity.Id[]) {
      count += lookup[key]?.records?.length ?? 0
    }
    return count
  }

  /**
   * Evicts least-recently-accessed entity entries from the lookup until the
   * total record count is at or below {@link MAX_CACHED_RECORDS}.
   */
  export function evictLookup(lookup: Lookup): Lookup {
    if (totalRecords(lookup) <= MAX_CACHED_RECORDS) {
      return lookup
    }

    const keys = Object.keys(lookup) as ents.core.dms.models.Entity.Id[]
    const sorted = keys.sort(
      (a, b) => (lookup[a]?.lastAccessed ?? 0) - (lookup[b]?.lastAccessed ?? 0),
    )

    const next: Lookup = {}
    let remaining = 0
    // Walk from most-recently-accessed to least, keeping entries until we hit the cap
    for (let i = sorted.length - 1; i >= 0; i--) {
      const key = sorted[i]
      const recordCount = lookup[key]?.records?.length ?? 0
      if (remaining + recordCount <= MAX_CACHED_RECORDS) {
        next[key] = lookup[key]
        remaining += recordCount
      }
    }
    return next
  }
}
